import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';

/** WI 35983: the sales glossary (Stephen Peifer's Commercial and Sales Terminology). */
export const SALES_GLOSSARY = 'sales';

/** One glossary term as Sparta's GlossaryController returns it. */
export interface GlossaryTerm {
  glossary: string;
  termKey: string;
  term: string;
  aliases: string[];
  category: string | null;
  definition: string;
  revision: string | null;
  isActive: boolean;
  modifiedDate: string;
}

/** The editable fields of a term; the glossary and key come from the route. */
export interface GlossaryTermSave {
  term: string;
  aliases: string[];
  category: string | null;
  definition: string;
  revision: string | null;
  isActive: boolean;
}

/**
 * WI 35983: runtime-editable glossaries stored in Sparta (dbo.GlossaryTerm). Reading is
 * open to any signed-in user and cached per glossary for the session, so every tip on a
 * page shares one request. A failed read yields an empty glossary (tips render without
 * their icon) and is not cached, so the next caller retries. Edits are gated server side
 * by the glossary's Software Security edit feature.
 */
@Injectable({ providedIn: 'root' })
export class GlossaryService {
  private readonly http: HttpClient;
  private readonly termsCache = new Map<
    string,
    Observable<ReadonlyMap<string, GlossaryTerm>>
  >();

  constructor(clientFactory: MyHttpClientFactory) {
    this.http = clientFactory.createHttpClient(
      environment.urls.spartaAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  /** Active terms keyed by termKey, fetched once per glossary. */
  public termsByKey(
    glossary: string
  ): Observable<ReadonlyMap<string, GlossaryTerm>> {
    const cached = this.termsCache.get(glossary);
    if (cached) return cached;

    const terms$ = this.http
      .get<GlossaryTerm[]>(`api/glossary/${encodeURIComponent(glossary)}`)
      .pipe(
        map(
          (terms) =>
            new Map((terms ?? []).map((t) => [t.termKey, t])) as ReadonlyMap<
              string,
              GlossaryTerm
            >
        ),
        catchError(() => {
          this.termsCache.delete(glossary);
          return of(new Map() as ReadonlyMap<string, GlossaryTerm>);
        }),
        shareReplay(1)
      );
    this.termsCache.set(glossary, terms$);
    return terms$;
  }

  /** Active terms in the order the server returns them. */
  public terms(glossary: string): Observable<GlossaryTerm[]> {
    return this.termsByKey(glossary).pipe(map((byKey) => [...byKey.values()]));
  }

  /** Whether the signed-in user may edit this glossary; false on any failure. */
  public canEdit(glossary: string): Observable<boolean> {
    return this.http
      .get<boolean>(`api/glossary/${encodeURIComponent(glossary)}/can-edit`)
      .pipe(
        map((allowed) => allowed === true),
        catchError(() => of(false))
      );
  }

  /** Every term, inactive included, for the editor. Needs the edit feature. */
  public allTermsForEditing(glossary: string): Observable<GlossaryTerm[]> {
    return this.http.get<GlossaryTerm[]>(
      `api/glossary/${encodeURIComponent(glossary)}/all`
    );
  }

  /** Saves one term and drops the cached glossary so tips pick up the edit. */
  public saveTerm(
    glossary: string,
    termKey: string,
    save: GlossaryTermSave
  ): Observable<GlossaryTerm> {
    return this.http
      .put<GlossaryTerm>(
        `api/glossary/${encodeURIComponent(glossary)}/terms/${encodeURIComponent(termKey)}`,
        save
      )
      .pipe(tap(() => this.termsCache.delete(glossary)));
  }
}
